/**
 * Grilla anual de casas. Cada fila = 1 lote; 12 columnas Ene-Dic +
 * estado + saldo. Celdas con color semántico según estado del mes.
 *
 * Sprint 17 Fase 2 — alineado con el handoff "Vista Casas":
 * - 🟢 verde + ✓ = pagado
 * - 🔴 rojo + ✗ = no pagado
 * - 🟣 morado + C = compensación
 * - ⬜ dashed + · = futuro
 */
import type {
  CasaAnualResumen,
  EstadoMes,
} from '../../lib/api/casas-tesoreria';
import { formatCelular } from '../../lib/format';
import { CeldaMes, ESTILO_MES, MES_CORTO } from '../ui/CeldaMes';
import { ESTILO_ESTADO_CASA } from '../ui/EstadoCasaBadge';
import { Monto, Num } from './Monto';

// Sprint 17 S17-Z5: estado "al_dia_pendiente_semestre" (amarillo) para casas
// que pagaron hasta el mes corriente pero con saldo del semestre restante.
// El mapa color+etiqueta vive en `ui/EstadoCasaBadge` (compartido con el
// reporte de ficha de la Comisión) para mantener una sola fuente de verdad.

export interface GrillaCasasAnualProps {
  casas: CasaAnualResumen[];
  onCasaClick: (casa: CasaAnualResumen) => void;
}

// Columnas de estado de cuenta (reformulado 2026-06): el orden de izquierda
// a derecha cuenta la historia — Deuda AA (apertura) → Abono (lo pagado este
// año a esa deuda) → Saldo a hoy (total exigible MTD, neto de saldo a favor).
// Layout: 250px(Casa) + 12×32px(meses) + 95px(Estado) + 100px(Deuda AA)
// + 100px(Abono) + 115px(Saldo a hoy) = 1044px.
const GRILLA_COLS = 'grid-cols-[250px_repeat(12,32px)_95px_100px_100px_115px]';

export function GrillaCasasAnual({ casas, onCasaClick }: GrillaCasasAnualProps) {
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[1200px]">
        {/* Leyenda */}
        <div className="flex items-center gap-4 mb-3 text-[11px] flex-wrap">
          {(Object.keys(ESTILO_MES) as EstadoMes[]).map((k) => (
            <div key={k} className="flex items-center gap-1.5">
              <CeldaMes estado={k} small />
              <span className="text-text-muted">{ESTILO_MES[k].label}</span>
            </div>
          ))}
        </div>

        {/* Header */}
        <div
          className={`grid ${GRILLA_COLS} gap-1 text-[10px] uppercase tracking-wide text-text-muted border-b border-white/[0.06] pb-2 mb-1`}
        >
          <div>Casa</div>
          {MES_CORTO.map((m, i) => (
            <div key={i} className="text-center">
              {m}
            </div>
          ))}
          <div className="text-center">Estado</div>
          <div
            className="text-right"
            title="Deuda de años anteriores al 1-ene (apertura)"
          >
            $Deuda AA
          </div>
          <div
            className="text-right"
            title="Abonado este año a la deuda de años anteriores"
          >
            $Abono
          </div>
          <div
            className="text-right"
            title="Saldo total exigible a hoy (mes a mes), neto de saldo a favor"
          >
            $Saldo a hoy
          </div>
        </div>

        {/* Filas */}
        {casas.map((c) => {
          const estado = ESTILO_ESTADO_CASA[c.estado];
          return (
            <button
              key={c.lote_id}
              type="button"
              onClick={() => onCasaClick(c)}
              className={`w-full grid ${GRILLA_COLS} gap-1 items-center py-1.5 px-1 -mx-1 rounded text-left border-b border-white/[0.04] hover:bg-white/[0.03] focus:outline-none focus-visible:ring-1 focus-visible:ring-orange`}
            >
              <div>
                <div className="flex items-center gap-1.5">
                  <Num className="text-sm font-medium text-text-primary">
                    {c.identificador}
                  </Num>
                  {c.sector && (
                    <span className="text-[9px] uppercase tracking-wide text-text-muted bg-white/[0.04] rounded-sm px-1 py-px">
                      {c.sector}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-text-muted truncate max-w-[240px]">
                  {c.titular_nombre || '—'}
                </div>
                {c.titular_celular && (
                  <div className="text-[10px] text-text-muted/80">
                    <Num>{formatCelular(c.titular_celular)}</Num>
                  </div>
                )}
              </div>
              {c.meses.map((m, i) => (
                <CeldaMes key={i} estado={m} />
              ))}
              <div className="flex justify-center">
                <span
                  className="text-[10px] px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: estado.bg,
                    color: estado.text,
                  }}
                >
                  {estado.label}
                </span>
              </div>
              {/* $ Deuda AA — apertura (histórico, color neutro) */}
              <div
                className="text-right text-xs text-text-secondary"
                title="Deuda de años anteriores al 1-ene (apertura)"
              >
                {c.deuda_anio_anterior > 0 ? (
                  <Monto value={c.deuda_anio_anterior} />
                ) : (
                  '—'
                )}
              </div>
              {/* $ Abono — abonado este año a la deuda AA (verde) */}
              <div
                className={`text-right text-xs ${
                  c.abono_anio_anterior > 0
                    ? 'text-[#5fd9b1] font-medium'
                    : 'text-text-muted'
                }`}
                title="Abonado este año a la deuda de años anteriores"
              >
                {c.abono_anio_anterior > 0 ? (
                  <Monto value={c.abono_anio_anterior} />
                ) : (
                  '—'
                )}
              </div>
              {/* $ Saldo a hoy — total exigible MTD neto de saldo a favor.
                  >0 debe (rojo), <0 a favor (verde, "−$"), 0 al día. */}
              <div
                className={`text-right text-xs font-medium ${
                  c.saldo_a_hoy > 0
                    ? 'text-red-400'
                    : c.saldo_a_hoy < 0
                      ? 'text-[#5fd9b1]'
                      : 'text-text-secondary'
                }`}
                title={
                  c.saldo_a_hoy < 0
                    ? 'Saldo a favor disponible'
                    : 'Saldo total exigible a hoy (neto de saldo a favor)'
                }
              >
                {c.saldo_a_hoy > 0 ? (
                  <Monto value={c.saldo_a_hoy} />
                ) : c.saldo_a_hoy < 0 ? (
                  <>
                    −<Monto value={-c.saldo_a_hoy} />
                  </>
                ) : (
                  '—'
                )}
              </div>
            </button>
          );
        })}

        {casas.length === 0 && (
          <div className="text-sm text-text-muted py-8 text-center">
            Sin casas para mostrar con los filtros actuales.
          </div>
        )}
      </div>
    </div>
  );
}
